/**
 * Blog Posts API service
 */

import { fetchStrapi, getStrapiMediaUrl, type StrapiResponse } from './strapi';

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  author: string;
  authorBio: string;
  authorImageUrl: string;
  date: string;
  category: string;
  categorySlug: string;
  imageUrl: string;
  readTime: string;
  content?: string;
}

export type BlogPostsResponse = StrapiResponse<BlogPost[]>;

/**
 * Calculate read time based on content length
 * Assumes average reading speed of 200 words per minute for German text
 */
function calculateReadTime(content: string): string {
  if (!content) return '1 min';
  
  // Strip HTML tags for word count
  const textContent = content.replace(/<[^>]*>/g, '');
  const wordCount = textContent.split(/\s+/).filter(word => word.length > 0).length;
  const minutes = Math.ceil(wordCount / 200);
  
  return `${minutes} min`;
}

/**
 * Fetch blog posts from Strapi
 */
export async function fetchBlogPosts(limit?: number): Promise<BlogPost[]> {
  try {
    let endpoint = '/blog-posts?populate=*&sort=datum:desc';
    if (limit) {
      endpoint += `&pagination[limit]=${limit}`;
    }

    const response = await fetchStrapi<BlogPostsResponse>(endpoint);

    if (!response.data) {
      return [];
    }

    return response.data.map((item: any) => {
      const categoryData = item.kategorie;

      return {
        id: item.id,
        title: item.titel || '',
        slug: item.slug || '',
        excerpt: item.auszug || '',
        author: item.autor || '',
        authorBio: item.autorBio || '',
        authorImageUrl: item.autorBild?.url
          ? getStrapiMediaUrl(item.autorBild.url)
          : '',
        date: item.datum || '',
        category: categoryData?.name || '',
        categorySlug: categoryData?.slug || '',
        imageUrl: item.bild?.url
          ? getStrapiMediaUrl(item.bild.url)
          : '',
        readTime: calculateReadTime(item.inhalt),
        content: item.inhalt || '',
      };
    });
  } catch (error) {
    console.error('Error fetching blog posts from Strapi:', error);
    return [];
  }
}

/**
 * Fetch a single blog post by ID or slug
 */
export async function fetchBlogPost(idOrSlug: number | string): Promise<BlogPost | null> {
  try {
    let endpoint: string;
    
    if (typeof idOrSlug === 'number') {
      endpoint = `/blog-posts/${idOrSlug}?populate=*`;
    } else {
      // Fetch by slug
      endpoint = `/blog-posts?filters[slug][$eq]=${idOrSlug}&populate=*`;
    }
    
    const response = await fetchStrapi<any>(endpoint);

    // Handle array response for slug queries
    const item = Array.isArray(response.data) ? response.data[0] : response.data;

    if (!item) {
      return null;
    }

    const categoryData = item.kategorie;

    return {
      id: item.id,
      title: item.titel || '',
      slug: item.slug || '',
      excerpt: item.auszug || '',
      author: item.autor || '',
      authorBio: item.autorBio || '',
      authorImageUrl: item.autorBild?.url
        ? getStrapiMediaUrl(item.autorBild.url)
        : '',
      date: item.datum || '',
      category: categoryData?.name || '',
      categorySlug: categoryData?.slug || '',
      imageUrl: item.bild?.url
        ? getStrapiMediaUrl(item.bild.url)
        : '',
      readTime: calculateReadTime(item.inhalt),
      content: item.inhalt || '',
    };
  } catch (error) {
    console.error('Error fetching blog post from Strapi:', error);
    return null;
  }
}

/**
 * Fetch blog posts by category
 */
export async function fetchBlogPostsByCategory(categorySlug: string): Promise<BlogPost[]> {
  try {
    const endpoint = `/blog-posts?filters[kategorie][slug][$eq]=${categorySlug}&populate=*&sort=datum:desc`;

    const response = await fetchStrapi<BlogPostsResponse>(endpoint);

    if (!response.data) {
      return [];
    }

    return response.data.map((item: any) => {
      const categoryData = item.kategorie;

      return {
        id: item.id,
        title: item.titel || '',
        slug: item.slug || '',
        excerpt: item.auszug || '',
        author: item.autor || '',
        authorBio: item.autorBio || '',
        authorImageUrl: item.autorBild?.url
          ? getStrapiMediaUrl(item.autorBild.url)
          : '',
        date: item.datum || '',
        category: categoryData?.name || '',
        categorySlug: categoryData?.slug || '',
        imageUrl: item.bild?.url
          ? getStrapiMediaUrl(item.bild.url)
          : '',
        readTime: calculateReadTime(item.inhalt),
        content: item.inhalt || '',
      };
    });
  } catch (error) {
    console.error('Error fetching blog posts by category from Strapi:', error);
    return [];
  }
}
